import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

const UPLOADED_PHOTO = "https://static.readdy.ai/image/aab8598c00afda4d0b512811ae1e1dec/1880e2bc82fc5020eaf06e3fdd92164c.jpeg";

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
];

const stats = [
  { value: '10+', labelKey: 'stat_years' },
  { value: '50K+', labelKey: 'stat_pallets' },
  { value: '3', labelKey: 'stat_grades' },
  { value: '100%', labelKey: 'stat_quality' },
];

const products = [
  {
    id: 1,
    gradeKey: 'Grade A',
    titleKey: 'form_product_a',
    descKey: 'grade_a_desc',
    img: 'https://static.readdy.ai/image/aab8598c00afda4d0b512811ae1e1dec/9b340ad5763236b594c0cf9fc081388b.jpeg',
    badge: 'PREMIUM',
    badgeColor: 'bg-yellow-500 text-black',
    quickSpecs: { dimensions: '1200 × 800 mm', loadCapacity: 'Up to 1500 kg', woodType: 'Hardwood/Softwood' },
    qualityBadges: [
      { icon: 'ri-shield-check-line', label: 'EPAL Certified' },
      { icon: 'ri-checkbox-circle-line', label: 'Inspected' },
      { icon: 'ri-store-3-line', label: 'In Stock' },
    ],
    detailImages: [
      'https://static.readdy.ai/image/aab8598c00afda4d0b512811ae1e1dec/9b340ad5763236b594c0cf9fc081388b.jpeg',
      'https://public.readdy.ai/ai/img_res/56a8c43826cabf3162b34c61d88bbb8e.jpg',
      'https://readdy.ai/api/search-image?query=stacked%20grade%20A%20euro%20pallets%20neat%20rows%20warehouse%20storage%20facility%20Belgium%20logistics&width=800&height=600&seq=ga3&orientation=landscape',
    ],
    specs: [
      { label: 'Dimensions', value: '1200 × 800 mm (EUR/EPAL)' },
      { label: 'Load Capacity', value: 'Up to 1500 kg dynamic' },
      { label: 'Certification', value: 'EPAL certified' },
      { label: 'Condition', value: 'Excellent — minimal wear' },
      { label: 'Wood Type', value: 'Hardwood / Softwood' },
      { label: 'Repair Status', value: 'No repairs — original condition' },
      { label: 'Availability', value: 'In stock — large quantities' },
      { label: 'Min. Order Qty', value: 'Contact us for details' },
    ],
    features: ['Minimal wear & tear', 'EPAL certified', 'Structurally sound', 'Ready for immediate use'],
    fullDesc: 'Our Grade A used Euro pallets represent the highest quality in the second-hand pallet market. Each pallet is individually hand-inspected by our trained staff to ensure it meets strict EPAL standards.',
    pricing: [
      { range: '10-100', price: 14, unit: '€/pallet' },
      { range: '100-500', price: 12, unit: '€/pallet' },
      { range: '500-5000', price: 10.50, unit: '€/pallet' },
      { range: '5000+', price: 9.50, unit: '€/pallet' },
    ],
  },
  {
    id: 2,
    gradeKey: 'Grade B',
    titleKey: 'form_product_b',
    descKey: 'grade_b_desc',
    img: 'https://public.readdy.ai/ai/img_res/6203e656-81f6-4e73-888e-80c3b1d5925f.png',
    badge: 'VALUE',
    badgeColor: 'bg-amber-700 text-yellow-100',
    quickSpecs: { dimensions: '1200 × 800 mm', loadCapacity: 'Up to 1000 kg', woodType: 'Hardwood/Softwood' },
    qualityBadges: [
      { icon: 'ri-checkbox-circle-line', label: 'Inspected' },
      { icon: 'ri-price-tag-3-line', label: 'Best Value' },
      { icon: 'ri-store-3-line', label: 'In Stock' },
    ],
    detailImages: [
      'https://public.readdy.ai/ai/img_res/c43abe88-17b3-4fd9-91f1-c6a56352eaa2.png',
      'https://readdy.ai/api/search-image?query=grade%20B%20euro%20pallets%20stacked%20warehouse%20cost%20effective%20logistics%20Belgium%20pallet%20supplier&width=800&height=600&seq=gb3&orientation=landscape',
      'https://readdy.ai/api/search-image?query=wooden%20euro%20pallet%20grade%20B%20inspection%20quality%20check%20warehouse%20floor%20Belgium&width=800&height=600&seq=gb4&orientation=landscape',
    ],
    specs: [
      { label: 'Dimensions', value: '1200 × 800 mm (EUR/EPAL)' },
      { label: 'Load Capacity', value: 'Up to 1000 kg dynamic' },
      { label: 'Condition', value: 'Good — minor cosmetic wear' },
      { label: 'Wood Type', value: 'Hardwood / Softwood' },
      { label: 'Repair Status', value: 'Minor repairs may be present' },
      { label: 'Certification', value: 'Quality inspected' },
      { label: 'Availability', value: 'In stock — large quantities' },
      { label: 'Min. Order Qty', value: 'Contact us for details' },
    ],
    features: ['Functional & reliable', 'Quality inspected', 'Economical choice', 'Good load capacity'],
    fullDesc: 'Grade B pallets offer the best value for money for standard warehousing and logistics operations. These pallets may show minor cosmetic wear or small repairs, but are fully structurally sound and safe for use.',
    pricing: [
      { range: '10-100', price: 12, unit: '€/pallet' },
      { range: '100-500', price: 10, unit: '€/pallet' },
      { range: '500-5000', price: 9, unit: '€/pallet' },
      { range: '5000+', price: 8, unit: '€/pallet' },
    ],
  },
  {
    id: 3,
    gradeKey: 'New',
    titleKey: 'form_product_new',
    descKey: 'grade_new_desc',
    img: 'https://public.readdy.ai/ai/img_res/a0b5b946-f08e-472c-9146-e6f01453ec3f.png',
    badge: 'NEW',
    badgeColor: 'bg-green-700 text-yellow-100',
    quickSpecs: { dimensions: '1200 × 800 mm', loadCapacity: 'Up to 2000 kg', woodType: 'Virgin timber' },
    qualityBadges: [
      { icon: 'ri-shield-star-line', label: 'ISPM15' },
      { icon: 'ri-global-line', label: 'Export Ready' },
      { icon: 'ri-tools-line', label: 'Custom Sizes' },
    ],
    detailImages: [
      'https://public.readdy.ai/ai/img_res/a0b5b946-f08e-472c-9146-e6f01453ec3f.png',
      'https://static.readdy.ai/image/aab8598c00afda4d0b512811ae1e1dec/1880e2bc82fc5020eaf06e3fdd92164c.jpeg',
      'https://static.readdy.ai/image/aab8598c00afda4d0b512811ae1e1dec/dd1c04ea87f4a956044dc6b2cb3824c3.jpeg',
    ],
    specs: [
      { label: 'Standard Size', value: '1200 × 800 mm (EUR)' },
      { label: 'Custom Sizes', value: 'Available on request' },
      { label: 'Load Capacity', value: 'Up to 2000 kg dynamic' },
      { label: 'Certification', value: 'ISPM15 heat-treated & stamped' },
      { label: 'Wood Type', value: 'Virgin hardwood / softwood' },
      { label: 'Condition', value: 'Brand new — unused' },
      { label: 'Repair Status', value: 'N/A — new product' },
      { label: 'Lead Time', value: 'Contact us for availability' },
    ],
    features: ['Premium quality timber', 'ISPM15 compliant', 'Custom sizes available', 'Export ready'],
    fullDesc: 'Our new wooden pallets are manufactured from virgin timber to the highest quality standards. Every pallet is ISPM15 heat-treated and stamped, making them fully compliant for international export.',
    pricing: [
      { range: '10-100', price: 24, unit: '€/pallet' },
      { range: '100-500', price: 19, unit: '€/pallet' },
      { range: '500-5000', price: 16, unit: '€/pallet' },
      { range: '5000+', price: 14, unit: '€/pallet' },
    ],
  },
];

const LOGO_URL = "https://public.readdy.ai/ai/img_res/1465fc3d-9fcb-4ff8-a972-fea1715c1e21.png";

type Product = typeof products[0];

function LanguageSwitcher() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (code: string) => {
    i18n.changeLanguage(code);
    document.documentElement.dir = code === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = code;
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm font-bold text-gray-700 hover:border-yellow-500 hover:bg-yellow-50 transition-all cursor-pointer whitespace-nowrap bg-white"
      >
        <span className="text-base">{currentLang.flag}</span>
        <span className="text-xs tracking-widest uppercase">{currentLang.code.toUpperCase()}</span>
        <i className={`ri-arrow-down-s-line text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}></i>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-44 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-yellow-50 transition-all cursor-pointer text-left ${i18n.language === lang.code ? 'bg-yellow-50 font-bold text-gray-900' : 'text-gray-700'}`}
            >
              <span className="text-base">{lang.flag}</span>
              <span>{lang.label}</span>
              {i18n.language === lang.code && <i className="ri-check-line text-yellow-600 ml-auto"></i>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductModal({ product, onClose, scrollToBanking }: { product: Product; onClose: () => void; scrollToBanking: () => void }) {
  const { t } = useTranslation();
  const [activeImg, setActiveImg] = useState(0);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const scrollToContact = () => {
    onClose();
    setTimeout(() => {
      const contactSection = document.getElementById('contact');
      if (contactSection) contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleOrderNow = () => {
    onClose();
    // This will be handled by the parent component
    setTimeout(() => {
      const orderEvent = new CustomEvent('orderProduct', { 
        detail: { ...product, selectedQuantity: quantity } 
      });
      window.dispatchEvent(orderEvent);
    }, 100);
  };

  // Calculate current price based on quantity
  const getCurrentPricing = (qty: number) => {
    return product.pricing.find(tier => {
      if (tier.range.includes('+')) {
        const minQty = parseInt(tier.range.replace('+', ''));
        return qty >= minQty;
      }
      const [min, max] = tier.range.split('-').map(n => parseInt(n));
      return qty >= min && qty <= max;
    }) || product.pricing[0];
  };

  const currentPricing = getCurrentPricing(quantity);
  const totalPrice = currentPricing.price * quantity;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
      <div className="relative bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-white rounded-full border border-gray-200 text-gray-600 hover:text-gray-900 transition-all cursor-pointer shadow-md">
          <i className="ri-close-line text-xl"></i>
        </button>
        <div className="grid lg:grid-cols-2 gap-0">
          <div className="p-6 bg-gray-50">
            <div className="relative rounded-xl overflow-hidden border border-gray-200 mb-4 shadow-sm" style={{ aspectRatio: '4/3' }}>
              <img src={product.detailImages[activeImg]} alt={product.gradeKey} className="w-full h-full object-cover object-top transition-all duration-300" />
              <span className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-black tracking-widest uppercase ${product.badgeColor} shadow-md`}>{product.badge}</span>
            </div>
            <div className="mb-3">
              <p className="text-gray-500 text-xs tracking-widest uppercase mb-2 font-medium"><i className="ri-image-line mr-1"></i>{t('product_view_angles')}</p>
              <div className="flex gap-3">
                {product.detailImages.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} className={`flex-1 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${activeImg === i ? 'border-yellow-500 shadow-md' : 'border-gray-200 hover:border-yellow-400'}`} style={{ aspectRatio: '4/3' }}>
                    <img src={img} alt={`View ${i + 1}`} className="w-full h-full object-cover object-top" />
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="p-6 lg:pl-6 flex flex-col">
            <div className="mb-2"><span className="text-yellow-600 text-xs tracking-widest uppercase font-bold">{product.gradeKey}</span></div>
            <h2 className="text-2xl font-black text-gray-900 mb-4 leading-tight">{t(product.titleKey)}</h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-6">{product.fullDesc}</p>
            
            {/* Pricing Tiers */}
            <div className="mb-6">
              <h4 className="text-gray-900 text-xs tracking-widest uppercase font-black mb-3">
                <i className="ri-price-tag-3-line mr-2"></i>Volume Pricing
              </h4>
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                <div className="grid grid-cols-2 gap-3">
                  {product.pricing.map((tier, i) => (
                    <div key={i} className={`flex items-center justify-between p-3 rounded-md border ${currentPricing.range === tier.range ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200 bg-white'} transition-all`}>
                      <div>
                        <div className="text-gray-600 text-xs">{tier.range} pcs</div>
                        <div className={`font-bold text-sm ${currentPricing.range === tier.range ? 'text-yellow-600' : 'text-gray-900'}`}>€{tier.price}/pallet</div>
                      </div>
                      {currentPricing.range === tier.range && (
                        <i className="ri-check-line text-yellow-600"></i>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h4 className="text-gray-900 text-xs tracking-widest uppercase font-black mb-3">{t('product_tech_specs')}</h4>
              <div className="grid grid-cols-2 gap-2">
                {product.specs.map((s, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                    <div className="text-gray-500 text-xs mb-0.5">{s.label}</div>
                    <div className="text-gray-900 text-xs font-bold">{s.value}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mb-6">
              <h4 className="text-gray-900 text-xs tracking-widest uppercase font-black mb-3">{t('product_key_features')}</h4>
              <ul className="space-y-2">
                {product.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-gray-700 text-sm">
                    <i className="ri-checkbox-circle-fill text-yellow-500 text-base"></i>{f}
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Quantity Selector & Payment Options */}
            <div className="mb-6 bg-gray-50 rounded-lg border border-gray-200 p-4">
              <h4 className="text-gray-900 text-xs tracking-widest uppercase font-black mb-3">
                <i className="ri-shopping-cart-line mr-2"></i>Select Quantity & Payment
              </h4>
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center border border-gray-300 rounded-md bg-white">
                  <button 
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all cursor-pointer"
                  >
                    <i className="ri-subtract-line"></i>
                  </button>
                  <input 
                    type="number" 
                    min="1" 
                    value={quantity} 
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-20 px-3 py-2 text-center border-l border-r border-gray-300 focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all"
                  />
                  <button 
                    onClick={() => setQuantity(quantity + 1)}
                    className="px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all cursor-pointer"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
                <div className="flex-1">
                  <div className="text-gray-500 text-xs">Total Price</div>
                  <div className="text-gray-900 font-bold text-lg">€{totalPrice.toFixed(2)}</div>
                  <div className="text-gray-500 text-xs">({quantity} × €{currentPricing.price})</div>
                </div>
              </div>
              
              {/* Payment Methods */}
              <div className="mt-4">
                <div className="text-gray-700 text-xs font-bold mb-2">Payment Methods Available</div>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={scrollToBanking} className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-3 py-2 hover:border-yellow-500 hover:bg-yellow-50 transition-all cursor-pointer">
                    <i className="ri-bank-card-line text-blue-600"></i>
                    <span className="text-xs font-bold text-gray-700">Card Payment</span>
                  </button>
                  <button onClick={scrollToBanking} className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-3 py-2 hover:border-yellow-500 hover:bg-yellow-50 transition-all cursor-pointer">
                    <i className="ri-google-fill text-red-500"></i>
                    <span className="text-xs font-bold text-gray-700">Google Pay</span>
                  </button>
                  <button onClick={scrollToBanking} className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-3 py-2 hover:border-yellow-500 hover:bg-yellow-50 transition-all cursor-pointer">
                    <i className="ri-paypal-fill text-blue-700"></i>
                    <span className="text-xs font-bold text-gray-700">PayPal</span>
                  </button>
                  <button onClick={scrollToBanking} className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-3 py-2 hover:border-yellow-500 hover:bg-yellow-50 transition-all cursor-pointer">
                    <i className="ri-bank-line text-purple-600"></i>
                    <span className="text-xs font-bold text-gray-700">Bancontact</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-auto flex gap-3">
              <button onClick={handleOrderNow} className="flex-1 text-center px-6 py-3 bg-yellow-500 text-black font-black rounded-md hover:bg-yellow-400 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase shadow-md">
                <i className="ri-shopping-cart-line mr-2"></i>Order €{totalPrice.toFixed(2)}
              </button>
              <button onClick={scrollToContact} className="flex-1 text-center px-6 py-3 border-2 border-gray-300 text-gray-700 font-black rounded-md hover:border-yellow-500 hover:bg-yellow-50 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase">{t('product_request_quote')}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function OrderModal({ product, onClose }: { product: Product | null; onClose: () => void }) {
  const { t } = useTranslation();
  const [orderData, setOrderData] = useState({
    companyName: '', contactName: '', email: '', phone: '',
    product: product ? t(product.titleKey) : '',
    quantity: product?.selectedQuantity?.toString() || '1', deliveryAddress: '', preferredDate: '', notes: '',
  });
  const [orderStatus, setOrderStatus] = useState<'form' | 'summary' | 'sending' | 'success'>('form');

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    if (product) {
      setOrderData(prev => ({ 
        ...prev, 
        product: t(product.titleKey),
        quantity: product.selectedQuantity?.toString() || '1'
      }));
    }
    return () => { document.body.style.overflow = ''; };
  }, [product, t]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'notes' && value.length > 500) return;
    setOrderData(prev => ({ ...prev, [name]: value }));
  };

  const handleContinue = (e: React.FormEvent) => { e.preventDefault(); setOrderStatus('summary'); };

  const handleSubmitOrder = async () => {
    setOrderStatus('sending');
    try {
      const body = new URLSearchParams();
      Object.entries(orderData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch('https://readdy.ai/api/form/d6idbb857726vudt88hg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) { setOrderStatus('success'); } else { setOrderStatus('form'); }
    } catch { setOrderStatus('form'); }
  };

  const referenceNumber = `QUO-${Date.now().toString().slice(-8)}`;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
      <div className="relative bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-white rounded-full border border-gray-200 text-gray-600 hover:text-gray-900 transition-all cursor-pointer shadow-md">
          <i className="ri-close-line text-xl"></i>
        </button>
        <div className="p-8">
          {orderStatus === 'form' && (
            <>
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center bg-yellow-500 rounded-full"><i className="ri-shopping-cart-line text-black text-lg"></i></div>
                  <h2 className="text-3xl font-black text-gray-900">{t('order_title')}</h2>
                </div>
                <p className="text-gray-600 text-sm">{t('order_subtitle')}</p>
              </div>
              <form data-readdy-form onSubmit={handleContinue} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-xs tracking-widest uppercase mb-2 font-bold">{t('order_company')} *</label>
                    <input name="companyName" required value={orderData.companyName} onChange={handleChange} placeholder={t('order_company_ph')} className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all" />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-xs tracking-widest uppercase mb-2 font-bold">{t('order_contact')} *</label>
                    <input name="contactName" required value={orderData.contactName} onChange={handleChange} placeholder={t('order_contact_ph')} className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-xs tracking-widest uppercase mb-2 font-bold">{t('form_email')} *</label>
                    <input name="email" type="email" required value={orderData.email} onChange={handleChange} placeholder="your@email.com" className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all" />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-xs tracking-widest uppercase mb-2 font-bold">{t('form_phone')} *</label>
                    <input name="phone" type="tel" required value={orderData.phone} onChange={handleChange} placeholder="+32 ..." className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-xs tracking-widest uppercase mb-2 font-bold">{t('form_product')} *</label>
                    <select name="product" required value={orderData.product} onChange={handleChange} className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 text-sm focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all cursor-pointer">
                      <option value="">{t('form_product_placeholder')}</option>
                      <option value="Used Euro Pallets — Grade A">{t('form_product_a')}</option>
                      <option value="Used Euro Pallets — Grade B">{t('form_product_b')}</option>
                      <option value="New Wooden Pallets">{t('form_product_new')}</option>
                      <option value="Mixed Order">{t('form_product_mixed')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 text-xs tracking-widest uppercase mb-2 font-bold">{t('order_quantity')} *</label>
                    <input name="quantity" type="number" required min="1" value={orderData.quantity} onChange={handleChange} placeholder={t('order_quantity_ph')} className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all" />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-700 text-xs tracking-widest uppercase mb-2 font-bold">{t('order_address')} *</label>
                  <input name="deliveryAddress" required value={orderData.deliveryAddress} onChange={handleChange} placeholder={t('order_address_ph')} className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all" />
                </div>
                <div>
                  <label className="block text-gray-700 text-xs tracking-widest uppercase mb-2 font-bold">{t('order_date')}</label>
                  <input name="preferredDate" type="date" value={orderData.preferredDate} onChange={handleChange} className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 text-sm focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all cursor-pointer" />
                </div>
                <div>
                  <label className="block text-gray-700 text-xs tracking-widest uppercase mb-2 font-bold">{t('order_notes')}</label>
                  <textarea name="notes" value={orderData.notes} onChange={handleChange} maxLength={500} rows={3} placeholder={t('order_notes_ph')} className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all resize-none"></textarea>
                  <div className="text-right text-gray-500 text-xs mt-1">{orderData.notes.length}/500</div>
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={onClose} className="flex-1 px-6 py-4 border-2 border-gray-300 text-gray-700 font-black rounded-md hover:border-gray-400 hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase">{t('order_cancel')}</button>
                  <button type="submit" className="flex-1 px-6 py-4 bg-yellow-500 text-black font-black rounded-md hover:bg-yellow-400 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase shadow-md">{t('order_review')}</button>
                </div>
              </form>
            </>
          )}
          {orderStatus === 'summary' && (
            <>
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center bg-yellow-500 rounded-full"><i className="ri-file-list-3-line text-black text-lg"></i></div>
                  <h2 className="text-3xl font-black text-gray-900">{t('order_summary_title')}</h2>
                </div>
                <p className="text-gray-600 text-sm">{t('order_summary_subtitle')}</p>
              </div>
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-6 mb-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-gray-900 text-xs tracking-widest uppercase font-black mb-4">{t('order_company_info')}</h4>
                    <div className="space-y-3">
                      <div><div className="text-gray-500 text-xs mb-1">{t('order_company_name_label')}</div><div className="text-gray-900 font-bold">{orderData.companyName}</div></div>
                      <div><div className="text-gray-500 text-xs mb-1">{t('order_contact_label')}</div><div className="text-gray-900 font-bold">{orderData.contactName}</div></div>
                      <div><div className="text-gray-500 text-xs mb-1">{t('order_email_label')}</div><div className="text-gray-900 font-bold break-all">{orderData.email}</div></div>
                      <div><div className="text-gray-500 text-xs mb-1">{t('order_phone_label')}</div><div className="text-gray-900 font-bold">{orderData.phone}</div></div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-gray-900 text-xs tracking-widest uppercase font-black mb-4">{t('order_details')}</h4>
                    <div className="space-y-3">
                      <div><div className="text-gray-500 text-xs mb-1">{t('order_product_label')}</div><div className="text-gray-900 font-bold">{orderData.product}</div></div>
                      <div><div className="text-gray-500 text-xs mb-1">{t('order_qty_label')}</div><div className="text-gray-900 font-bold">{orderData.quantity} {t('order_qty_unit')}</div></div>
                      <div><div className="text-gray-500 text-xs mb-1">{t('order_address_label')}</div><div className="text-gray-900 font-bold">{orderData.deliveryAddress}</div></div>
                      {orderData.preferredDate && <div><div className="text-gray-500 text-xs mb-1">{t('order_date_label')}</div><div className="text-gray-900 font-bold">{orderData.preferredDate}</div></div>}
                    </div>
                  </div>
                </div>
                {orderData.notes && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="text-gray-500 text-xs mb-2">{t('order_notes_label')}</div>
                    <div className="text-gray-900 text-sm">{orderData.notes}</div>
                  </div>
                )}
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-yellow-600 text-lg mt-0.5"></i>
                  <div className="text-sm text-gray-700"><strong className="font-bold">{t('order_next_steps')}</strong> {t('order_next_steps_desc')}</div>
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setOrderStatus('form')} className="flex-1 px-6 py-4 border-2 border-gray-300 text-gray-700 font-black rounded-md hover:border-gray-400 hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase"><i className="ri-arrow-left-line mr-2"></i>{t('order_edit')}</button>
                <button onClick={handleSubmitOrder} className="flex-1 px-6 py-4 bg-yellow-500 text-black font-black rounded-md hover:bg-yellow-400 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase shadow-md">{t('order_confirm')}</button>
              </div>
            </>
          )}
          {orderStatus === 'sending' && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mb-6"></div>
              <h4 className="text-gray-900 font-black text-xl mb-2">{t('order_submitting')}</h4>
              <p className="text-gray-600 text-sm">{t('order_submitting_desc')}</p>
            </div>
          )}
          {orderStatus === 'success' && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-green-50 rounded-full border-4 border-green-200 mb-6"><i className="ri-check-line text-green-600 text-4xl"></i></div>
              <h4 className="text-gray-900 font-black text-2xl mb-3">{t('order_success_title')}</h4>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-6 py-3 mb-6">
                <div className="text-gray-500 text-xs tracking-widest uppercase mb-1">{t('order_ref_label')}</div>
                <div className="text-gray-900 font-black text-xl">{referenceNumber}</div>
              </div>
              <p className="text-gray-600 text-sm max-w-md mb-8 leading-relaxed">{t('order_success_desc')}</p>
              <div className="flex gap-3">
                <button onClick={onClose} className="px-8 py-3 bg-yellow-500 text-black font-black rounded-md hover:bg-yellow-400 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase shadow-md">{t('order_close')}</button>
                <a href="tel:+32487261970" className="px-8 py-3 border-2 border-gray-300 text-gray-700 font-black rounded-md hover:border-yellow-500 hover:bg-yellow-50 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase"><i className="ri-phone-line mr-2"></i>{t('product_call_us')}</a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', product: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [activeTab, setActiveTab] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [orderProduct, setOrderProduct] = useState<Product | null>(null);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [productQuantities, setProductQuantities] = useState<{[key: number]: number}>({ 1: 1, 2: 1, 3: 1 });

  const scrollToBanking = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Add a slight delay to ensure the section is visible, then highlight banking info
      setTimeout(() => {
        const bankingSection = contactSection.querySelector('.bg-gray-50.rounded-xl.border.border-gray-200.p-6');
        if (bankingSection) {
          bankingSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add temporary highlight effect
          bankingSection.classList.add('ring-2', 'ring-yellow-500', 'ring-opacity-50');
          setTimeout(() => {
            bankingSection.classList.remove('ring-2', 'ring-yellow-500', 'ring-opacity-50');
          }, 3000);
        }
      }, 500);
    }
  };

  // Add Schema.org JSON-LD structured data
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': `${siteUrl}/#business`,
      name: 'PEL Partner',
      description: 'Premium Euro pallet supplier in Antwerp, Belgium. EPAL-certified Grade A & B used pallets and new ISPM15 wooden pallets for logistics and warehousing.',
      url: siteUrl,
      telephone: '+32487261970',
      email: 'pelpartnercompany@gmail.com',
      logo: 'https://public.readdy.ai/ai/img_res/1465fc3d-9fcb-4ff8-a972-fea1715c1e21.png',
      image: 'https://static.readdy.ai/image/aab8598c00afda4d0b512811ae1e1dec/1880e2bc82fc5020eaf06e3fdd92164c.jpeg',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Antwerp',
        addressRegion: 'Flanders',
        addressCountry: 'BE',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: '51.2194',
        longitude: '4.4025',
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '08:00',
          closes: '18:00',
        },
      ],
      priceRange: '€€',
      currenciesAccepted: 'EUR',
      paymentAccepted: 'Bank Transfer, Credit Card, PayPal, Google Pay, Bancontact',
      areaServed: { '@type': 'Country', name: 'Belgium' },
      sameAs: [],
    };

    const productSchemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'Grade A Used Euro Pallets',
        description: 'Premium EPAL-certified Grade A used Euro pallets. Minimal wear, food-grade ready, structurally sound. Available in large quantities from Antwerp, Belgium.',
        image: 'https://static.readdy.ai/image/aab8598c00afda4d0b512811ae1e1dec/9b340ad5763236b594c0cf9fc081388b.jpeg',
        url: `${siteUrl}/pallets/grade-a`,
        brand: { '@type': 'Brand', name: 'PEL Partner' },
        category: 'Industrial Pallets',
        offers: {
          '@type': 'AggregateOffer',
          priceCurrency: 'EUR',
          lowPrice: '9.5',
          highPrice: '14',
          offerCount: '4',
          availability: 'https://schema.org/InStock',
          seller: { '@type': 'Organization', name: 'PEL Partner' },
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'Grade B Used Euro Pallets',
        description: 'Cost-effective Grade B used Euro pallets. Quality inspected, structurally sound, ideal for standard warehousing and logistics. Available from Antwerp, Belgium.',
        image: 'https://public.readdy.ai/ai/img_res/6203e656-81f6-4e73-888e-80c3b1d5925f.png',
        url: `${siteUrl}/pallets/grade-b`,
        brand: { '@type': 'Brand', name: 'PEL Partner' },
        category: 'Industrial Pallets',
        offers: {
          '@type': 'AggregateOffer',
          priceCurrency: 'EUR',
          lowPrice: '8',
          highPrice: '12',
          offerCount: '4',
          availability: 'https://schema.org/InStock',
          seller: { '@type': 'Organization', name: 'PEL Partner' },
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'New Wooden Euro Pallets',
        description: 'Brand new ISPM15 heat-treated wooden pallets made from virgin timber. Export ready, custom sizes available. Supplied from Antwerp, Belgium.',
        image: 'https://public.readdy.ai/ai/img_res/a0b5b946-f08e-472c-9146-e6f01453ec3f.png',
        url: `${siteUrl}/pallets/new`,
        brand: { '@type': 'Brand', name: 'PEL Partner' },
        category: 'Industrial Pallets',
        offers: {
          '@type': 'AggregateOffer',
          priceCurrency: 'EUR',
          lowPrice: '14',
          highPrice: '24',
          offerCount: '4',
          availability: 'https://schema.org/InStock',
          seller: { '@type': 'Organization', name: 'PEL Partner' },
        },
      },
    ];

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is the difference between Grade A and Grade B pallets?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Grade A pallets are in excellent condition with minimal wear, EPAL certified, and suitable for food-grade use. Grade B pallets show minor cosmetic wear or small repairs but are fully structurally sound and offer great value for standard operations.',
          },
        },
        {
          '@type': 'Question',
          name: 'Do you deliver pallets throughout Belgium?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, we deliver throughout Belgium including Antwerp, Brussels, Ghent, Bruges, Liège and all other regions. Contact us for delivery quotes and scheduling.',
          },
        },
        {
          '@type': 'Question',
          name: 'Are your new pallets ISPM15 compliant for export?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, all our new wooden pallets are ISPM15 heat-treated and stamped, making them fully compliant for international export and shipping.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is the minimum order quantity for pallets?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Our volume pricing starts from 10 pallets. Grade A: €14/pallet (10–100), €12 (100–500), €10.50 (500–5000), €9.50 (5000+). Grade B: €12 (10–100), €10 (100–500), €9 (500–5000), €8 (5000+). New pallets: €24 (10–100), €19 (100–500), €16 (500–5000), €14 (5000+).',
          },
        },
        {
          '@type': 'Question',
          name: 'What payment methods do you accept?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'We accept bank transfer (EUR IBAN: LT71 3250 0690 5068 8936, BIC: REVOLT21), credit card, Google Pay, PayPal, and Bancontact.',
          },
        },
        {
          '@type': 'Question',
          name: 'Are your Grade A pallets EPAL certified?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, our Grade A pallets are EPAL certified. Each pallet is individually hand-inspected by our trained staff to ensure it meets strict EPAL standards with minimal wear and full structural integrity.',
          },
        },
      ],
    };

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/#webpage`,
      name: 'PEL Partner – Euro Pallets Antwerp Belgium',
      description: 'Premium Euro pallet supplier in Antwerp, Belgium. EPAL-certified Grade A & B used pallets and new ISPM15 wooden pallets.',
      url: siteUrl,
      inLanguage: 'en',
      isPartOf: { '@type': 'WebSite', url: siteUrl, name: 'PEL Partner' },
      about: { '@id': `${siteUrl}/#business` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        ],
      },
    };

    const schemas = [localBusinessSchema, ...productSchemas, faqSchema, webPageSchema];
    const scriptElements: HTMLScriptElement[] = [];

    schemas.forEach((schema, index) => {
      const existing = document.getElementById(`schema-home-${index}`);
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      script.id = `schema-home-${index}`;
      document.head.appendChild(script);
      scriptElements.push(script);
    });

    return () => {
      scriptElements.forEach((s) => { if (s.parentNode) s.parentNode.removeChild(s); });
    };
  }, []);

  const referenceNumber = `QUO-${Date.now().toString().slice(-8)}`;

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setFormStatus('sending');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch('https://readdy.ai/api/form/d6icpqo57726vudt88bg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) { setFormStatus('success'); setFormData({ name: '', email: '', phone: '', product: '', message: '' }); }
      else { setFormStatus('error'); }
    } catch { setFormStatus('error'); }
  };

  const faqKeys = [
    { q: 'faq_q1', a: 'faq_a1' }, { q: 'faq_q2', a: 'faq_a2' },
    { q: 'faq_q3', a: 'faq_a3' }, { q: 'faq_q4', a: 'faq_a4' },
    { q: 'faq_q5', a: 'faq_a5' }, { q: 'faq_q6', a: 'faq_a6' },
    { q: 'faq_q7', a: 'faq_a7' }, { q: 'faq_q8', a: 'faq_a8' },
  ];

  const navLinks = [
    { labelKey: 'nav_used_pallets', href: '#used-pallets' },
    { labelKey: 'nav_new_pallets', href: '#new-pallets' },
    { labelKey: 'nav_services', href: '#services' },
    { labelKey: 'nav_about', href: '#about' },
    { labelKey: 'nav_faq', href: '#faq' },
    { labelKey: 'nav_contact', href: '#contact' },
  ];

  const handleQuantityChange = (productId: number, newQuantity: number) => {
    setProductQuantities(prev => ({ ...prev, [productId]: Math.max(1, newQuantity) }));
  };

  const handleOrderProduct = (product: Product) => {
    setOrderProduct({ ...product, selectedQuantity: productQuantities[product.id] });
  };

  // Calculate current price for a product based on quantity
  const getCurrentPrice = (product: Product, qty: number) => {
    const pricing = product.pricing.find(tier => {
      if (tier.range.includes('+')) {
        const minQty = parseInt(tier.range.replace('+', ''));
        return qty >= minQty;
      }
      const [min, max] = tier.range.split('-').map(n => parseInt(n));
      return qty >= min && qty <= max;
    });
    return pricing || product.pricing[0];
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-gray-800 font-sans">
      {selectedProduct && <ProductModal product={selectedProduct} onClose={() => setSelectedProduct(null)} scrollToBanking={scrollToBanking} />}
      {orderProduct && <OrderModal product={orderProduct} onClose={() => setOrderProduct(null)} />}

      {/* ── NAVBAR ── */}
      <nav className={`fixed w-full z-50 transition-all duration-400 ${scrolled ? 'bg-white/98 backdrop-blur-md shadow-lg' : 'bg-transparent'}`}>
        <div className="px-6 lg:px-16 py-4 flex items-center justify-between">
          <a href="#hero" className="flex items-center gap-3 cursor-pointer">
            <div className="h-20 w-20 flex items-center justify-center">
              <img
                src="https://public.readdy.ai/ai/img_res/1465fc3d-9fcb-4ff8-a972-fea1715c1e21.png"
                alt="PEL Partner Logo"
                className="h-20 w-20 object-contain drop-shadow-lg"
              />
            </div>
            <span className={`text-3xl font-bold tracking-widest uppercase transition-colors ${scrolled ? 'text-gray-900' : 'text-white'}`}>PEL Partner</span>
          </a>
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(item => (
              <a key={item.labelKey} href={item.href} className={`text-sm tracking-widest uppercase transition-colors whitespace-nowrap cursor-pointer font-medium ${scrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/90 hover:text-white'}`}>{t(item.labelKey)}</a>
            ))}
            <a href="#contact" className="px-5 py-2 bg-yellow-500 text-black text-sm font-bold rounded-md hover:bg-yellow-400 transition-all whitespace-nowrap cursor-pointer tracking-wider uppercase shadow-md">{t('nav_get_quote')}</a>
            <LanguageSwitcher />
          </div>
          <div className="md:hidden flex items-center gap-3">
            <LanguageSwitcher />
            <button className="w-8 h-8 flex items-center justify-center cursor-pointer" onClick={() => setMenuOpen(!menuOpen)}>
              <i className={`ri-${menuOpen ? 'close' : 'menu'}-line text-2xl ${scrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 px-6 pb-6 flex flex-col gap-4">
            {navLinks.map(item => (
              <a key={item.labelKey} href={item.href} onClick={() => setMenuOpen(false)} className="text-gray-700 hover:text-gray-900 uppercase tracking-widest text-sm cursor-pointer font-medium">{t(item.labelKey)}</a>
            ))}
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/aab8598c00afda4d0b512811ae1e1dec/1880e2bc82fc5020eaf06e3fdd92164c.jpeg"
            alt="PEL Partner Warehouse"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/20 via-transparent to-black/20"></div>
        </div>
        <div className="relative z-10 text-center px-6 w-full max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-yellow-500/60 rounded-md text-yellow-400 text-xs tracking-widest uppercase mb-8 bg-black/30 backdrop-blur-sm">
            <i className="ri-map-pin-2-line"></i> {t('hero_location')}
          </div>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-yellow-400 mb-6 leading-none tracking-tight">
            {t('hero_title_line1')}<br /><span className="text-white">{t('hero_title_line2')}</span>
          </h1>
          <p className="text-lg md:text-xl text-white/90 mb-10 max-w-2xl mx-auto leading-relaxed">{t('hero_subtitle')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#contact" className="px-10 py-4 bg-yellow-500 text-black font-black rounded-md hover:bg-yellow-400 transition-all whitespace-nowrap cursor-pointer text-sm tracking-widest uppercase shadow-xl">{t('hero_cta_quote')}</a>
            <a href="#used-pallets" className="px-10 py-4 border-2 border-white/80 text-white font-bold rounded-md hover:border-white hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer text-sm tracking-widest uppercase backdrop-blur-sm">{t('hero_cta_products')}</a>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white/60 text-2xl"></i>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <section className="bg-yellow-500 py-8 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <div key={i} className="text-center">
              <div className="text-3xl md:text-4xl font-black text-black">{s.value}</div>
              <div className="text-black/70 text-xs tracking-widest uppercase mt-1 font-bold">{t(s.labelKey)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── GALLERY STRIP ── */}
      <section className="py-12 px-6 lg:px-16 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-yellow-600 text-xs tracking-widest uppercase mb-2 font-bold">{t('gallery_label')}</p>
            <h3 className="text-2xl font-black text-gray-900">{t('gallery_title')}</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { img: 'https://static.readdy.ai/image/aab8598c00afda4d0b512811ae1e1dec/9b340ad5763236b594c0cf9fc081388b.jpeg', labelKey: 'gallery_grade_a' },
              { img: 'https://readdy.ai/api/search-image?query=used%20euro%20pallet%20grade%20B%20wooden%20pallet%20slight%20wear%20marks%20warehouse%20Belgium%20logistics%20supply&width=600&height=400&seq=gb1&orientation=landscape', labelKey: 'gallery_grade_b' },
              { img: 'https://static.readdy.ai/image/aab8598c00afda4d0b512811ae1e1dec/efdd10b95615d4918760d3d2efc476a9.jpeg', labelKey: 'gallery_new' },
              { img: 'https://static.readdy.ai/image/aab8598c00afda4d0b512811ae1e1dec/dd1c04ea87f4a956044dc6b2cb3824c3.jpeg', labelKey: 'gallery_custom' },
            ].map((item, i) => (
              <div key={i} className="group relative rounded-xl overflow-hidden border border-gray-200 hover:border-yellow-400 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                <img src={item.img} alt={t(item.labelKey)} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                {/* Watermark */}
                <div className="absolute bottom-8 right-2 flex items-center gap-1 bg-black/30 backdrop-blur-sm rounded-md px-2 py-1">
                  <img src={LOGO_URL} alt="PEL Partner" className="h-5 w-5 object-contain opacity-90" />
                  <span className="text-white text-[10px] font-black tracking-widest uppercase opacity-90">PEL Partner</span>
                </div>
                <div className="absolute bottom-3 left-3 right-3"><p className="text-white text-sm font-bold tracking-wide">{t(item.labelKey)}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="py-20 px-6 lg:px-16 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-yellow-600 text-xs tracking-widest uppercase mb-3 font-bold">{t('how_label')}</p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">{t('how_title')}</h2>
            <div className="w-16 h-1 bg-yellow-500 mb-6"></div>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm leading-relaxed">{t('how_subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', icon: 'ri-search-line', titleKey: 'how_step1_title', descKey: 'how_step1_desc' },
              { step: '02', icon: 'ri-file-list-3-line', titleKey: 'how_step2_title', descKey: 'how_step2_desc' },
              { step: '03', icon: 'ri-truck-line', titleKey: 'how_step3_title', descKey: 'how_step3_desc' },
            ].map((item, i) => (
              <div key={i} className="relative bg-white rounded-xl border border-gray-200 p-8 hover:border-yellow-400 transition-all group shadow-sm">
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-black font-black text-lg">{item.step}</span>
                </div>
                <div className="w-16 h-16 flex items-center justify-center bg-yellow-50 rounded-full border border-yellow-200 mb-6 group-hover:bg-yellow-100 transition-all">
                  <i className={`${item.icon} text-yellow-600 text-2xl`}></i>
                </div>
                <h3 className="text-gray-900 font-black text-lg mb-3">{t(item.titleKey)}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{t(item.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── USED PALLETS ── */}
      <section id="used-pallets" className="py-24 px-6 lg:px-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-yellow-600 text-xs tracking-widest uppercase mb-3 font-bold">{t('used_label')}</p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">{t('used_title')}</h2>
            <div className="w-16 h-1 bg-yellow-500 mb-6"></div>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm leading-relaxed">{t('used_subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {products.filter(p => p.id <= 2).map(p => {
              const currentPrice = getCurrentPrice(p, productQuantities[p.id]);
              const totalPrice = currentPrice.price * productQuantities[p.id];
              
              return (
                <div key={p.id} className="group bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-yellow-400 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                  <div className="relative h-60 w-full overflow-hidden cursor-pointer" onClick={() => setSelectedProduct(p)}>
                    <img src={p.img} alt={t(p.titleKey)} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <span className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-black tracking-widest uppercase ${p.badgeColor} shadow-md`}>{p.badge}</span>
                    <span className="absolute bottom-4 left-4 text-white text-xs font-bold tracking-widest uppercase bg-black/50 px-2 py-1 rounded">{p.gradeKey}</span>
                    {/* Watermark */}
                    <div className="absolute bottom-4 right-3 flex items-center gap-1 bg-black/30 backdrop-blur-sm rounded-md px-2 py-1">
                      <img src={LOGO_URL} alt="PEL Partner" className="h-5 w-5 object-contain opacity-90" />
                      <span className="text-white text-[10px] font-black tracking-widest uppercase opacity-90">PEL Partner</span>
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 transition-all duration-300 bg-yellow-500 text-black text-xs font-black tracking-widest uppercase px-4 py-2 rounded-full shadow-lg">{t('product_view_details')}</span>
                    </div>
                  </div>
                  <div className="p-7">
                    <h3 className="text-xl font-black text-gray-900 mb-3">{t(p.titleKey)}</h3>
                    <p className="text-gray-600 text-sm mb-5 leading-relaxed">{p.fullDesc}</p>
                    
                    {/* Pricing Display */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-5">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-gray-900 text-xs tracking-widest uppercase font-black">
                          <i className="ri-price-tag-3-line mr-2 text-yellow-600"></i>Current Price
                        </h4>
                        <div className="text-right">
                          <div className="text-2xl font-black text-gray-900">€{totalPrice.toFixed(2)}</div>
                          <div className="text-gray-600 text-xs">({productQuantities[p.id]} × €{currentPrice.price})</div>
                        </div>
                      </div>
                      <div className="text-gray-600 text-xs">Volume pricing available - order more to save!</div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 mb-5 border border-gray-200">
                      <h4 className="text-gray-900 text-xs tracking-widest uppercase font-black mb-3">{t('product_quick_specs')}</h4>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm"><span className="text-gray-600">{t('product_dimensions')}:</span><span className="text-gray-900 font-bold">{p.quickSpecs.dimensions}</span></div>
                        <div className="flex items-center justify-between text-sm"><span className="text-gray-600">{t('product_load')}:</span><span className="text-gray-900 font-bold">{p.quickSpecs.loadCapacity}</span></div>
                        <div className="flex items-center justify-between text-sm"><span className="text-gray-600">{t('product_wood')}:</span><span className="text-gray-900 font-bold">{p.quickSpecs.woodType}</span></div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mb-5 pb-5 border-b border-gray-200">
                      {p.qualityBadges.map((badge, i) => (
                        <div key={i} className="flex items-center gap-1.5 bg-yellow-50 border border-yellow-200 rounded-full px-3 py-1.5">
                          <i className={`${badge.icon} text-yellow-600 text-sm`}></i>
                          <span className="text-xs font-bold text-gray-700">{badge.label}</span>
                        </div>
                      ))}
                    </div>

                    {/* Quantity Selector & Payment Options */}
                    <div className="mb-5 bg-gray-50 rounded-lg border border-gray-200 p-4">
                      <h4 className="text-gray-900 text-xs tracking-widest uppercase font-black mb-3">
                        <i className="ri-stack-line mr-2"></i>Quantity & Payment
                      </h4>
                      
                      {/* Quantity Selection */}
                      <div className="mb-4">
                        <div className="text-gray-700 text-xs font-bold mb-2">Select Quantity</div>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center border border-gray-300 rounded-md bg-white">
                            <button 
                              onClick={() => handleQuantityChange(p.id, productQuantities[p.id] - 1)}
                              className="px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all cursor-pointer"
                            >
                              <i className="ri-subtract-line"></i>
                            </button>
                            <input 
                              type="number" 
                              min="1" 
                              value={productQuantities[p.id]} 
                              onChange={(e) => handleQuantityChange(p.id, parseInt(e.target.value) || 1)}
                              className="w-20 px-3 py-2 text-center border-l border-r border-gray-300 focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all"
                            />
                            <button 
                              onClick={() => handleQuantityChange(p.id, productQuantities[p.id] + 1)}
                              className="px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all cursor-pointer"
                            >
                              <i className="ri-add-line"></i>
                            </button>
                          </div>
                          <div className="flex-1">
                            <div className="text-gray-500 text-xs">Total</div>
                            <div className="text-gray-900 font-bold">€{totalPrice.toFixed(2)}</div>
                          </div>
                        </div>
                      </div>

                      {/* Payment Methods */}
                      <div>
                        <div className="text-gray-700 text-xs font-bold mb-2">Payment Methods Available</div>
                        <div className="grid grid-cols-2 gap-2">
                          <button onClick={scrollToBanking} className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-3 py-2 hover:border-yellow-500 hover:bg-yellow-50 transition-all cursor-pointer">
                            <i className="ri-bank-card-line text-blue-600"></i>
                            <span className="text-xs font-bold text-gray-700">Card Payment</span>
                          </button>
                          <button onClick={scrollToBanking} className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-3 py-2 hover:border-yellow-500 hover:bg-yellow-50 transition-all cursor-pointer">
                            <i className="ri-google-fill text-red-500"></i>
                            <span className="text-xs font-bold text-gray-700">Google Pay</span>
                          </button>
                          <button onClick={scrollToBanking} className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-3 py-2 hover:border-yellow-500 hover:bg-yellow-50 transition-all cursor-pointer">
                            <i className="ri-paypal-fill text-blue-700"></i>
                            <span className="text-xs font-bold text-gray-700">PayPal</span>
                          </button>
                          <button onClick={scrollToBanking} className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-3 py-2 hover:border-yellow-500 hover:bg-yellow-50 transition-all cursor-pointer">
                            <i className="ri-bank-line text-purple-600"></i>
                            <span className="text-xs font-bold text-gray-700">Bancontact</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <button onClick={() => handleOrderProduct(p)} className="flex-1 text-center px-6 py-3 bg-yellow-500 text-black font-black rounded-md hover:bg-yellow-400 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase shadow-md">
                        <i className="ri-shopping-cart-line mr-2"></i>Order €{totalPrice.toFixed(2)}
                      </button>
                      <button onClick={() => setSelectedProduct(p)} className="flex-1 text-center px-6 py-3 border-2 border-gray-300 text-gray-700 font-black rounded-md hover:border-yellow-500 hover:bg-yellow-50 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase">{t('product_view_details')}</button>
                    </div>
                    
                    {/* Add SEO-friendly link to dedicated page */}
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <a 
                        href={p.id === 1 ? '/pallets/grade-a' : '/pallets/grade-b'} 
                        className="flex items-center justify-center gap-2 text-yellow-600 hover:text-yellow-700 text-sm font-bold transition-colors cursor-pointer"
                      >
                        <i className="ri-external-link-line"></i>
                        View Full Page
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── NEW PALLETS ── */}
      <section id="new-pallets" className="py-24 px-6 lg:px-16 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-yellow-600 text-xs tracking-widest uppercase mb-3 font-bold">{t('new_label')}</p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">{t('new_title')}</h2>
            <div className="w-16 h-1 bg-yellow-500 mx-auto mb-6"></div>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm leading-relaxed">{t('new_subtitle')}</p>
          </div>
          <div className="max-w-2xl mx-auto">
            {products.filter(p => p.id === 3).map(p => {
              const currentPrice = getCurrentPrice(p, productQuantities[p.id]);
              const totalPrice = currentPrice.price * productQuantities[p.id];
              
              return (
                <div key={p.id} className="group bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-yellow-400 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                  <div className="relative h-80 w-full overflow-hidden cursor-pointer" onClick={() => setSelectedProduct(p)}>
                    <img src={p.img} alt={t(p.titleKey)} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <span className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-black tracking-widest uppercase ${p.badgeColor} shadow-md`}>{p.badge}</span>
                    <span className="absolute bottom-4 left-4 text-white text-xs font-bold tracking-widest uppercase bg-black/50 px-2 py-1 rounded">{p.gradeKey}</span>
                    {/* Watermark */}
                    <div className="absolute bottom-4 right-3 flex items-center gap-1 bg-black/30 backdrop-blur-sm rounded-md px-2 py-1">
                      <img src={LOGO_URL} alt="PEL Partner" className="h-5 w-5 object-contain opacity-90" />
                      <span className="text-white text-[10px] font-black tracking-widest uppercase opacity-90">PEL Partner</span>
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 transition-all duration-300 bg-yellow-500 text-black text-xs font-black tracking-widest uppercase px-4 py-2 rounded-full shadow-lg">{t('product_view_details')}</span>
                    </div>
                  </div>
                  <div className="p-7">
                    <h3 className="text-xl font-black text-gray-900 mb-3">{t(p.titleKey)}</h3>
                    <p className="text-gray-600 text-sm mb-5 leading-relaxed">{p.fullDesc}</p>
                    
                    {/* Pricing Display */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-5">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-gray-900 text-xs tracking-widest uppercase font-black">
                          <i className="ri-price-tag-3-line mr-2 text-green-600"></i>Current Price
                        </h4>
                        <div className="text-right">
                          <div className="text-2xl font-black text-gray-900">€{totalPrice.toFixed(2)}</div>
                          <div className="text-gray-600 text-xs">({productQuantities[p.id]} × €{currentPrice.price})</div>
                        </div>
                      </div>
                      <div className="text-gray-600 text-xs">Volume pricing available - order more to save!</div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4 mb-5 border border-gray-200">
                      <h4 className="text-gray-900 text-xs tracking-widest uppercase font-black mb-3">{t('product_quick_specs')}</h4>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm"><span className="text-gray-600">{t('product_dimensions')}:</span><span className="text-gray-900 font-bold">{p.quickSpecs.dimensions}</span></div>
                        <div className="flex items-center justify-between text-sm"><span className="text-gray-600">{t('product_load')}:</span><span className="text-gray-900 font-bold">{p.quickSpecs.loadCapacity}</span></div>
                        <div className="flex items-center justify-between text-sm"><span className="text-gray-600">{t('product_wood')}:</span><span className="text-gray-900 font-bold">{p.quickSpecs.woodType}</span></div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mb-5 pb-5 border-b border-gray-200">
                      {p.qualityBadges.map((badge, i) => (
                        <div key={i} className="flex items-center gap-1.5 bg-green-50 border border-green-200 rounded-full px-3 py-1.5">
                          <i className={`${badge.icon} text-green-600 text-sm`}></i>
                          <span className="text-xs font-bold text-gray-700">{badge.label}</span>
                        </div>
                      ))}
                    </div>

                    {/* Quantity Selector & Payment Options */}
                    <div className="mb-5 bg-gray-50 rounded-lg border border-gray-200 p-4">
                      <h4 className="text-gray-900 text-xs tracking-widest uppercase font-black mb-3">
                        <i className="ri-stack-line mr-2"></i>Quantity & Payment
                      </h4>
                      
                      {/* Quantity Selection */}
                      <div className="mb-4">
                        <div className="text-gray-700 text-xs font-bold mb-2">Select Quantity</div>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center border border-gray-300 rounded-md bg-white">
                            <button 
                              onClick={() => handleQuantityChange(p.id, productQuantities[p.id] - 1)}
                              className="px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all cursor-pointer"
                            >
                              <i className="ri-subtract-line"></i>
                            </button>
                            <input 
                              type="number" 
                              min="1" 
                              value={productQuantities[p.id]} 
                              onChange={(e) => handleQuantityChange(p.id, parseInt(e.target.value) || 1)}
                              className="w-20 px-3 py-2 text-center border-l border-r border-gray-300 focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors"
                            />
                            <button 
                              onClick={() => handleQuantityChange(p.id, productQuantities[p.id] + 1)}
                              className="px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all cursor-pointer"
                            >
                              <i className="ri-add-line"></i>
                            </button>
                          </div>
                          <div className="flex-1">
                            <div className="text-gray-500 text-xs">Total</div>
                            <div className="text-gray-900 font-bold">€{totalPrice.toFixed(2)}</div>
                          </div>
                        </div>
                      </div>

                      {/* Payment Methods */}
                      <div>
                        <div className="text-gray-700 text-xs font-bold mb-2">Payment Methods Available</div>
                        <div className="grid grid-cols-2 gap-2">
                          <button onClick={scrollToBanking} className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-3 py-2 hover:border-yellow-500 hover:bg-yellow-50 transition-all cursor-pointer">
                            <i className="ri-bank-card-line text-blue-600"></i>
                            <span className="text-xs font-bold text-gray-700">Card Payment</span>
                          </button>
                          <button onClick={scrollToBanking} className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-3 py-2 hover:border-yellow-500 hover:bg-yellow-50 transition-all cursor-pointer">
                            <i className="ri-google-fill text-red-500"></i>
                            <span className="text-xs font-bold text-gray-700">Google Pay</span>
                          </button>
                          <button onClick={scrollToBanking} className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-3 py-2 hover:border-yellow-500 hover:bg-yellow-50 transition-all cursor-pointer">
                            <i className="ri-paypal-fill text-blue-700"></i>
                            <span className="text-xs font-bold text-gray-700">PayPal</span>
                          </button>
                          <button onClick={scrollToBanking} className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-3 py-2 hover:border-yellow-500 hover:bg-yellow-50 transition-all cursor-pointer">
                            <i className="ri-bank-line text-purple-600"></i>
                            <span className="text-xs font-bold text-gray-700">Bancontact</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <button onClick={() => handleOrderProduct(p)} className="flex-1 text-center px-6 py-3 bg-yellow-500 text-black font-black rounded-md hover:bg-yellow-400 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase shadow-md">
                        <i className="ri-shopping-cart-line mr-2"></i>Order €{totalPrice.toFixed(2)}
                      </button>
                      <button onClick={() => setSelectedProduct(p)} className="flex-1 text-center px-6 py-3 border-2 border-gray-300 text-gray-700 font-black rounded-md hover:border-yellow-500 hover:bg-yellow-50 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase">{t('product_view_details')}</button>
                    </div>
                    
                    {/* Add SEO-friendly link to dedicated page */}
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <a 
                        href="/pallets/new" 
                        className="flex items-center justify-center gap-2 text-yellow-600 hover:text-yellow-700 text-sm font-bold transition-colors cursor-pointer"
                      >
                        <i className="ri-external-link-line"></i>
                        View Full Page
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── COMPARISON TABLE ── */}
      <section className="py-24 px-6 lg:px-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-yellow-600 text-xs tracking-widest uppercase mb-3 font-bold">{t('compare_label')}</p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">{t('compare_title')}</h2>
            <div className="w-16 h-1 bg-yellow-500 mx-auto mb-6"></div>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm leading-relaxed">{t('compare_subtitle')}</p>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-lg">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr>
                  <th className="bg-gray-50 px-6 py-5 text-left text-xs tracking-widest uppercase font-black text-gray-500 w-1/4 border-b border-gray-200">
                    {t('compare_feature')}
                  </th>
                  {/* Grade A */}
                  <th className="px-6 py-5 text-center border-b border-gray-200 bg-yellow-50 w-1/4">
                    <div className="flex flex-col items-center gap-2">
                      <span className="inline-block px-3 py-1 bg-yellow-500 text-black text-xs font-black tracking-widest uppercase rounded-full">PREMIUM</span>
                      <span className="text-gray-900 font-black text-lg">{t('compare_grade_a')}</span>
                      <span className="text-yellow-600 font-black text-xl">€14<span className="text-sm font-bold text-gray-500">/pallet</span></span>
                    </div>
                  </th>
                  {/* Grade B */}
                  <th className="px-6 py-5 text-center border-b border-gray-200 bg-amber-50 w-1/4">
                    <div className="flex flex-col items-center gap-2">
                      <span className="inline-block px-3 py-1 bg-amber-700 text-yellow-100 text-xs font-black tracking-widest uppercase rounded-full">VALUE</span>
                      <span className="text-gray-900 font-black text-lg">{t('compare_grade_b')}</span>
                      <span className="text-amber-700 font-black text-xl">€12<span className="text-sm font-bold text-gray-500">/pallet</span></span>
                    </div>
                  </th>
                  {/* New */}
                  <th className="px-6 py-5 text-center border-b border-gray-200 bg-green-50 w-1/4">
                    <div className="flex flex-col items-center gap-2">
                      <span className="inline-block px-3 py-1 bg-green-700 text-yellow-100 text-xs font-black tracking-widest uppercase rounded-full">NEW</span>
                      <span className="text-gray-900 font-black text-lg">{t('compare_new')}</span>
                      <span className="text-green-700 font-black text-xl">€24<span className="text-sm font-bold text-gray-500">/pallet</span></span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  {
                    feature: t('compare_condition'),
                    a: t('compare_condition_a'),
                    b: t('compare_condition_b'),
                    n: t('compare_condition_new'),
                    icon: 'ri-checkbox-circle-line',
                  },
                  {
                    feature: t('compare_certification'),
                    a: t('compare_certification_a'),
                    b: t('compare_certification_b'),
                    n: t('compare_certification_new'),
                    icon: 'ri-shield-check-line',
                  },
                  {
                    feature: t('compare_load'),
                    a: t('compare_load_a'),
                    b: t('compare_load_b'),
                    n: t('compare_load_new'),
                    icon: 'ri-weight-line',
                  },
                  {
                    feature: t('compare_export'),
                    a: t('compare_export_a'),
                    b: t('compare_export_b'),
                    n: t('compare_export_new'),
                    icon: 'ri-global-line',
                  },
                  {
                    feature: t('compare_custom'),
                    a: t('compare_custom_a'),
                    b: t('compare_custom_b'),
                    n: t('compare_custom_new'),
                    icon: 'ri-ruler-2-line',
                    isBool: true,
                  },
                  {
                    feature: t('compare_availability'),
                    a: t('compare_availability_a'),
                    b: t('compare_availability_b'),
                    n: t('compare_availability_new'),
                    icon: 'ri-store-3-line',
                  },
                  {
                    feature: t('compare_best_for'),
                    a: t('compare_best_for_a'),
                    b: t('compare_best_for_b'),
                    n: t('compare_best_for_new'),
                    icon: 'ri-star-line',
                  },
                ].map((row, i) => (
                  <tr key={i} className={`border-b border-gray-100 transition-colors hover:bg-gray-50/60 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-gray-700 font-bold text-sm">
                        <div className="w-7 h-7 flex items-center justify-center bg-yellow-50 rounded-md border border-yellow-200 shrink-0">
                          <i className={`${row.icon} text-yellow-600 text-sm`}></i>
                        </div>
                        {row.feature}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center bg-yellow-50/40">
                      {row.isBool ? (
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 border border-gray-200">
                          <i className="ri-close-line text-gray-400 text-sm"></i>
                        </span>
                      ) : (
                        <span className="text-gray-800 text-sm font-medium">{row.a}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center bg-amber-50/30">
                      {row.isBool ? (
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 border border-gray-200">
                          <i className="ri-close-line text-gray-400 text-sm"></i>
                        </span>
                      ) : (
                        <span className="text-gray-800 text-sm font-medium">{row.b}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center bg-green-50/30">
                      {row.isBool ? (
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-green-100 border border-green-200">
                          <i className="ri-check-line text-green-600 text-sm"></i>
                        </span>
                      ) : (
                        <span className="text-gray-800 text-sm font-medium">{row.n}</span>
                      )}
                    </td>
                  </tr>
                ))}
                {/* CTA Row */}
                <tr className="bg-gray-50">
                  <td className="px-6 py-5 text-gray-500 text-xs tracking-widest uppercase font-black">{t('compare_starting_price')}</td>
                  <td className="px-6 py-5 text-center bg-yellow-50/40">
                    <a href="#used-pallets" className="inline-block px-5 py-2.5 bg-yellow-500 text-black font-black text-xs tracking-widest uppercase rounded-md hover:bg-yellow-400 transition-all whitespace-nowrap cursor-pointer shadow-sm">
                      {t('compare_cta')}
                    </a>
                  </td>
                  <td className="px-6 py-5 text-center bg-amber-50/30">
                    <a href="#used-pallets" className="inline-block px-5 py-2.5 bg-amber-700 text-yellow-100 font-black text-xs tracking-widest uppercase rounded-md hover:bg-amber-600 transition-all whitespace-nowrap cursor-pointer shadow-sm">
                      {t('compare_cta')}
                    </a>
                  </td>
                  <td className="px-6 py-5 text-center bg-green-50/30">
                    <a href="#new-pallets" className="inline-block px-5 py-2.5 bg-green-700 text-yellow-100 font-black text-xs tracking-widest uppercase rounded-md hover:bg-green-600 transition-all whitespace-nowrap cursor-pointer shadow-sm">
                      {t('compare_cta')}
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section id="services" className="py-24 px-6 lg:px-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-yellow-600 text-xs tracking-widest uppercase mb-3 font-bold">{t('services_label')}</p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">{t('services_title')}</h2>
            <div className="w-16 h-1 bg-yellow-500 mx-auto mb-6"></div>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm leading-relaxed">{t('services_subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: 'ri-stack-line', titleKey: 'service1_title', descKey: 'service1_desc' },
              { icon: 'ri-shopping-cart-2-line', titleKey: 'service2_title', descKey: 'service2_desc' },
              { icon: 'ri-ruler-2-line', titleKey: 'service3_title', descKey: 'service3_desc' },
              { icon: 'ri-truck-line', titleKey: 'service4_title', descKey: 'service4_desc' },
            ].map((service, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-8 hover:border-yellow-400 transition-all group shadow-sm">
                <div className="w-16 h-16 flex items-center justify-center bg-yellow-50 rounded-full border border-yellow-200 mb-6 group-hover:bg-yellow-100 transition-all">
                  <i className={`${service.icon} text-yellow-600 text-2xl`}></i>
                </div>
                <h3 className="text-gray-900 font-black text-lg mb-3">{t(service.titleKey)}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{t(service.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── QUALITY GRADES ── */}
      <section id="quality" className="py-24 px-6 lg:px-16 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-yellow-600 text-xs tracking-widest uppercase mb-3 font-bold">{t('quality_label')}</p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">{t('quality_title')}</h2>
            <div className="w-16 h-1 bg-yellow-500 mx-auto mb-6"></div>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm leading-relaxed">{t('quality_subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { grade: 'A', color: 'border-yellow-400', titleColor: 'text-yellow-600', bg: 'bg-yellow-50', labelKey: 'grade_a_label', points: ['No broken or missing boards','No cracks or splits in deck boards','All blocks intact and undamaged','EPAL stamp visible and valid','Clean, ready for food-grade use','Maximum load capacity maintained'] },
              { grade: 'B', color: 'border-amber-500', titleColor: 'text-amber-700', bg: 'bg-amber-50', labelKey: 'grade_b_label', points: ['Minor cosmetic wear allowed','Small repairs may be present','Structurally sound and safe','Good load-bearing capacity','Ideal for general warehousing','Cost-effective solution'] },
              { grade: 'NEW', color: 'border-green-500', titleColor: 'text-green-700', bg: 'bg-green-50', labelKey: 'grade_new_label', points: ['Virgin timber, no prior use','ISPM15 heat-treated & stamped','Custom dimensions available','Export & international compliant','Maximum strength & durability','Ideal for sensitive goods'] },
            ].map((g, i) => (
              <div key={i} className={`rounded-xl border-2 ${g.color} ${g.bg} p-8 shadow-sm`}>
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-14 h-14 rounded-full border-2 ${g.color} flex items-center justify-center bg-white`}>
                    <span className={`text-xl font-black ${g.titleColor}`}>{g.grade}</span>
                  </div>
                  <div>
                    <div className={`text-lg font-black ${g.titleColor}`}>Grade {g.grade}</div>
                    <div className="text-gray-600 text-xs tracking-widest uppercase">{t(g.labelKey)}</div>
                  </div>
                </div>
                <ul className="space-y-3">
                  {g.points.map((pt, j) => (
                    <li key={j} className="flex items-start gap-2 text-gray-700 text-sm">
                      <i className={`ri-check-double-line ${g.titleColor} mt-0.5`}></i>{pt}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section id="about" className="py-24 px-6 lg:px-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-yellow-600 text-xs tracking-widest uppercase mb-3 font-bold">{t('about_label')}</p>
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 leading-tight">{t('about_title')}</h2>
              <div className="w-16 h-1 bg-yellow-500 mb-8"></div>
              <p className="text-gray-600 text-base leading-relaxed mb-6">{t('about_p1')}</p>
              <p className="text-gray-600 text-base leading-relaxed mb-10">{t('about_p2')}</p>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { icon: 'ri-map-pin-2-line', title: t('contact_location_label'), val: t('about_location') },
                  { icon: 'ri-shield-check-line', title: t('stat_quality'), val: t('about_quality') },
                  { icon: 'ri-truck-line', title: 'Delivery', val: t('about_delivery') },
                  { icon: 'ri-customer-service-2-line', title: 'Support', val: t('about_support') },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-yellow-50 rounded-lg border border-yellow-200 shrink-0">
                      <i className={`${item.icon} text-yellow-600`}></i>
                    </div>
                    <div>
                      <div className="text-gray-900 font-bold text-sm">{item.title}</div>
                      <div className="text-gray-600 text-xs mt-0.5">{item.val}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="h-[500px] w-full rounded-xl overflow-hidden border border-gray-200 shadow-lg">
                <img src={UPLOADED_PHOTO} alt="PEL Partner warehouse" className="w-full h-full object-cover object-center" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent rounded-xl"></div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-yellow-500 text-black rounded-xl p-5 shadow-2xl">
                <div className="text-3xl font-black">10+</div>
                <div className="text-xs font-bold tracking-widest uppercase">{t('about_years')}</div>
              </div>
              <div className="absolute -top-6 -right-6 bg-white border-2 border-yellow-400 text-gray-900 rounded-xl p-5 shadow-2xl">
                <div className="text-3xl font-black text-yellow-600">50K+</div>
                <div className="text-xs font-bold tracking-widest uppercase text-gray-700">{t('about_supplied')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── VIDEOS ── */}
      <section id="videos" className="py-24 px-6 lg:px-16 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-yellow-600 text-xs tracking-widest uppercase mb-3 font-bold">{t('videos_label')}</p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">{t('videos_title')}</h2>
            <div className="w-16 h-1 bg-yellow-500 mx-auto mb-6"></div>
            <p className="text-gray-600 max-w-xl mx-auto text-sm">{t('videos_subtitle')}</p>
          </div>
          <div className="flex justify-center mb-10">
            <div className="inline-flex bg-white border border-gray-200 rounded-full px-1 py-1 gap-1 shadow-sm">
              {[t('tab_sorting'), t('tab_quality'), t('tab_delivery')].map((tab, i) => (
                <button key={i} onClick={() => setActiveTab(i)} className={`px-6 py-2 rounded-full text-sm font-bold tracking-wider uppercase transition-all whitespace-nowrap cursor-pointer ${activeTab === i ? 'bg-yellow-500 text-black shadow-md' : 'text-gray-600 hover:text-gray-900'}`}>{tab}</button>
              ))}
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-white shadow-lg" style={{ aspectRatio: '16/9' }}>
              {activeTab === 0 && (
                <div className="relative w-full h-full group cursor-pointer">
                  <img src="https://static.readdy.ai/image/aab8598c00afda4d0b512811ae1e1dec/9b340ad5763236b594c0cf9fc081388b.jpeg" alt="Pallet Sorting Process" className="w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-all flex items-center justify-center">
                    <div className="w-20 h-20 flex items-center justify-center bg-yellow-500 rounded-full shadow-2xl group-hover:scale-110 transition-transform">
                      <i className="ri-play-fill text-black text-3xl ml-1"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-white font-bold text-lg drop-shadow-lg">{t('video_sorting_title')}</p>
                    <p className="text-white/90 text-sm drop-shadow-lg">{t('video_sorting_desc')}</p>
                  </div>
                </div>
              )}
              {activeTab === 1 && <iframe className="w-full h-full" src="https://www.youtube.com/embed/ScMzIvxBSi4?rel=0&modestbranding=1" title="Quality Check" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>}
              {activeTab === 2 && <iframe className="w-full h-full" src="https://www.youtube.com/embed/3JZ_D3ELwOQ?rel=0&modestbranding=1" title="Delivery" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>}
            </div>
            <div className="space-y-6">
              {[
                { icon: 'ri-search-eye-line', titleKey: 'video_strict', descKey: 'video_strict_desc' },
                { icon: 'ri-stack-line', titleKey: 'video_sorted', descKey: 'video_sorted_desc' },
                { icon: 'ri-truck-line', titleKey: 'video_reliable', descKey: 'video_reliable_desc' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-5 bg-white rounded-xl border border-gray-200 hover:border-yellow-400 transition-all shadow-sm">
                  <div className="w-12 h-12 flex items-center justify-center bg-yellow-50 rounded-lg border border-yellow-200 shrink-0">
                    <i className={`${item.icon} text-yellow-600 text-xl`}></i>
                  </div>
                  <div>
                    <h4 className="text-gray-900 font-bold mb-1">{t(item.titleKey)}</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">{t(item.descKey)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY CHOOSE US ── */}
      <section className="py-20 px-6 lg:px-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-black text-gray-900 mb-4">{t('why_title')}</h2>
            <div className="w-16 h-1 bg-yellow-500 mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'ri-award-line', titleKey: 'why1_title', descKey: 'why1_desc' },
              { icon: 'ri-price-tag-3-line', titleKey: 'why2_title', descKey: 'why2_desc' },
              { icon: 'ri-time-line', titleKey: 'why3_title', descKey: 'why3_desc' },
              { icon: 'ri-handshake-line', titleKey: 'why4_title', descKey: 'why4_desc' },
            ].map((item, i) => (
              <div key={i} className="text-center p-8 bg-white rounded-xl border border-gray-200 hover:border-yellow-400 transition-all group shadow-sm">
                <div className="w-16 h-16 flex items-center justify-center bg-yellow-50 rounded-full border border-yellow-200 mx-auto mb-5 group-hover:bg-yellow-100 transition-all">
                  <i className={`${item.icon} text-yellow-600 text-2xl`}></i>
                </div>
                <h4 className="text-gray-900 font-black mb-3">{t(item.titleKey)}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{t(item.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DELIVERY AREAS ── */}
      <section className="py-20 px-6 lg:px-16 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-yellow-600 text-xs tracking-widest uppercase mb-3 font-bold">{t('delivery_label')}</p>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">{t('delivery_title')}</h2>
            <div className="w-16 h-1 bg-yellow-500 mx-auto"></div>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm leading-relaxed">{t('delivery_subtitle')}</p>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-8">
            {['Antwerp','Brussels','Ghent','Bruges','Liège','All Belgium'].map((city, i) => (
              <div key={i} className="flex items-center gap-3 bg-white border border-gray-200 rounded-full px-6 py-3 hover:border-yellow-400 hover:shadow-md transition-all">
                <div className="w-8 h-8 flex items-center justify-center bg-yellow-50 rounded-full border border-yellow-200">
                  <i className={`${i === 5 ? 'ri-map-2-fill' : 'ri-map-pin-2-fill'} text-yellow-600 text-sm`}></i>
                </div>
                <span className="text-gray-900 font-bold text-sm">{city}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="py-24 px-6 lg:px-16 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-yellow-600 text-xs tracking-widest uppercase mb-3 font-bold">{t('faq_label')}</p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">{t('faq_title')}</h2>
            <div className="w-16 h-1 bg-yellow-500 mx-auto mb-6"></div>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm leading-relaxed">{t('faq_subtitle')}</p>
          </div>
          <div className="space-y-4">
            {faqKeys.map((faq, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <button onClick={() => setOpenFaqIndex(openFaqIndex === i ? null : i)} className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-all cursor-pointer">
                  <span className="text-gray-900 font-bold text-base pr-4">{t(faq.q)}</span>
                  <div className="w-8 h-8 flex items-center justify-center bg-yellow-50 rounded-full border border-yellow-200 shrink-0">
                    <i className={`ri-${openFaqIndex === i ? 'subtract' : 'add'}-line text-yellow-600 text-sm transition-transform`}></i>
                  </div>
                </button>
                {openFaqIndex === i && (
                  <div className="px-6 pb-6 pt-0"><p className="text-gray-600 text-sm leading-relaxed">{t(faq.a)}</p></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section id="contact" className="py-24 px-6 lg:px-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-yellow-600 text-xs tracking-widest uppercase mb-3 font-bold">{t('contact_label')}</p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">{t('contact_title')}</h2>
            <div className="w-16 h-1 bg-yellow-500 mx-auto"></div>
          </div>
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-black text-gray-900 mb-6">{t('contact_info_title')}</h3>
              <div className="space-y-6 mb-10">
                {[
                  { icon: 'ri-phone-line', labelKey: 'contact_phone_label', val: '+32 487 26 19 70', href: 'tel:+32487261970' },
                  { icon: 'ri-mail-line', labelKey: 'contact_email_label', val: 'pelpartnercompany@gmail.com', href: 'mailto:pelpartnercompany@gmail.com' },
                  { icon: 'ri-map-pin-2-line', labelKey: 'contact_location_label', val: t('about_location'), href: undefined },
                  { icon: 'ri-time-line', labelKey: 'contact_hours_label', val: t('contact_hours_val'), href: undefined },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-yellow-50 rounded-lg border border-yellow-200 shrink-0">
                      <i className={`${item.icon} text-yellow-600 text-lg`}></i>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs tracking-widest uppercase mb-1">{t(item.labelKey)}</div>
                      {item.href ? (
                        <a href={item.href} className="text-gray-900 font-bold hover:text-yellow-600 transition-colors cursor-pointer break-all">{item.val}</a>
                      ) : (
                        <div className="text-gray-900 font-bold">{item.val}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Banking Information */}
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-6 mb-8">
                <h4 className="text-gray-900 font-black text-lg mb-4 flex items-center gap-2">
                  <i className="ri-bank-card-line text-yellow-600"></i>
                  Banking Information
                </h4>
                
                {/* EUR Account */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-blue-100 rounded-full">
                      <span className="text-blue-600 text-xs font-black">€</span>
                    </div>
                    <span className="text-gray-900 font-black text-base">Euro Account</span>
                  </div>
                  <div className="space-y-3 pl-10">
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Beneficiary Name</div>
                      <div className="text-gray-900 font-bold">Darav Khalid</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Bank Name</div>
                      <div className="text-gray-900 font-bold">Revolut Bank UAB</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Bank Address</div>
                      <div className="text-gray-900 font-bold">Konstitucijos ave. 21B, 08130, Vilnius, Lithuania</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">EUR IBAN</div>
                      <div className="text-gray-900 font-bold font-mono text-sm bg-white px-3 py-2 rounded border border-gray-200">LT71 3250 0690 5068 8936</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">BIC/SWIFT Code</div>
                      <div className="text-gray-900 font-bold font-mono text-sm bg-white px-3 py-2 rounded border border-gray-200">REVOLT21</div>
                    </div>
                  </div>
                </div>

                {/* GBP Account */}
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-purple-100 rounded-full">
                      <span className="text-purple-600 text-xs font-black">£</span>
                    </div>
                    <span className="text-gray-900 font-black text-base">British Pound Account</span>
                  </div>
                  <div className="space-y-3 pl-10">
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Beneficiary Name</div>
                      <div className="text-gray-900 font-bold">Darav Khalid</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Bank Name</div>
                      <div className="text-gray-900 font-bold">Revolut Ltd</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Bank Address</div>
                      <div className="text-gray-900 font-bold">30 South Colonnade, E14 5HX, London, United Kingdom</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Sort Code</div>
                      <div className="text-gray-900 font-bold font-mono text-sm bg-white px-3 py-2 rounded border border-gray-200">23-01-20</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Account Number</div>
                      <div className="text-gray-900 font-bold font-mono text-sm bg-white px-3 py-2 rounded border border-gray-200">87899767</div>
                    </div>
                  </div>
                </div>

                {/* Payment Methods */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-yellow-600 text-lg mt-0.5"></i>
                    <div className="text-sm text-gray-700">
                      <strong className="font-bold">Secure Payments:</strong> All transactions are processed securely. Contact us to arrange payment method for your order.
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <a href="tel:+32487261970" className="flex-1 text-center px-6 py-3 bg-yellow-500 text-black font-black rounded-md hover:bg-yellow-400 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase shadow-md">{t('contact_call_now')}</a>
                <a href="mailto:pelpartnercompany@gmail.com" className="flex-1 text-center px-6 py-3 border-2 border-gray-300 text-gray-700 font-black rounded-md hover:border-yellow-500 hover:bg-yellow-50 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase">{t('contact_email_us')}</a>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
              <h3 className="text-2xl font-black text-gray-900 mb-6">{t('contact_quote_title')}</h3>
              {formStatus === 'success' ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-20 h-20 flex items-center justify-center bg-green-50 rounded-full border-4 border-green-200 mb-6"><i className="ri-check-line text-green-600 text-4xl"></i></div>
                  <h4 className="text-gray-900 font-black text-2xl mb-3">{t('form_success_title')}</h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg px-6 py-3 mb-6">
                    <div className="text-gray-500 text-xs tracking-widest uppercase mb-1">{t('order_ref_label')}</div>
                    <div className="text-gray-900 font-black text-xl">{referenceNumber}</div>
                  </div>
                  <p className="text-gray-600 text-sm max-w-md mb-8 leading-relaxed">{t('form_success_desc')}</p>
                  <button onClick={() => setFormStatus('idle')} className="mt-6 px-6 py-3 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">{t('form_send_another')}</button>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleFormSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-600 text-xs tracking-widest uppercase mb-2 font-medium">{t('form_name')} *</label>
                      <input name="name" required value={formData.name} onChange={handleFormChange} placeholder={t('form_name')} className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors" />
                    </div>
                    <div>
                      <label className="block text-gray-600 text-xs tracking-widest uppercase mb-2 font-medium">{t('form_phone')}</label>
                      <input name="phone" value={formData.phone} onChange={handleFormChange} placeholder="+32 ..." className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-600 text-xs tracking-widest uppercase mb-2 font-medium">{t('form_email')} *</label>
                    <input name="email" type="email" required value={formData.email} onChange={handleFormChange} placeholder="your@email.com" className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-gray-600 text-xs tracking-widest uppercase mb-2 font-medium">{t('form_product')}</label>
                    <select name="product" value={formData.product} onChange={handleFormChange} className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 text-sm focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors cursor-pointer">
                      <option value="">{t('form_product_placeholder')}</option>
                      <option value="Grade A Used Pallets">{t('form_product_a')}</option>
                      <option value="Grade B Used Pallets">{t('form_product_b')}</option>
                      <option value="New Wooden Pallets">{t('form_product_new')}</option>
                      <option value="Mixed Order">{t('form_product_mixed')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-600 text-xs tracking-widest uppercase mb-2 font-medium">{t('form_message')}</label>
                    <textarea name="message" value={formData.message} onChange={handleFormChange} maxLength={500} rows={4} placeholder={t('form_message_placeholder')} className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors resize-none"></textarea>
                    <div className="text-right text-gray-500 text-xs mt-1">{formData.message.length}/500</div>
                  </div>
                  {formStatus === 'error' && <p className="text-red-600 text-sm">{t('form_error')}</p>}
                  <button type="submit" disabled={formStatus === 'sending'} className="w-full py-4 bg-yellow-500 text-black font-black rounded-md hover:bg-yellow-400 transition-all whitespace-nowrap cursor-pointer text-sm tracking-widest uppercase disabled:opacity-60 shadow-md">
                    {formStatus === 'sending' ? t('form_sending') : t('form_send')}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-[#2C2C2C] border-t border-gray-700 py-14 px-6 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-10 mb-10">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img src="https://public.readdy.ai/ai/img_res/1465fc3d-9fcb-4ff8-a972-fea1715c1e21.png" alt="PEL Partner Logo" className="h-10 w-10 object-contain" />
                <span className="text-xl font-black tracking-widest text-white uppercase">PEL Partner</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed max-w-xs">{t('footer_desc')}</p>
            </div>
            <div>
              <h4 className="text-white font-black text-xs tracking-widest uppercase mb-5">{t('footer_products')}</h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="/pallets/grade-a" className="hover:text-yellow-400 transition-colors cursor-pointer">{t('form_product_a')}</a></li>
                <li><a href="/pallets/grade-b" className="hover:text-yellow-400 transition-colors cursor-pointer">{t('form_product_b')}</a></li>
                <li><a href="/pallets/new" className="hover:text-yellow-400 transition-colors cursor-pointer">{t('form_product_new')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-black text-xs tracking-widest uppercase mb-5">{t('footer_contact')}</h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li className="flex items-center gap-2"><i className="ri-map-pin-line text-yellow-500"></i> Antwerp, Belgium</li>
                <li><a href="tel:+32487261970" className="flex items-center gap-2 hover:text-yellow-400 transition-colors cursor-pointer"><i className="ri-phone-line text-yellow-500"></i> +32 487 26 19 70</a></li>
                <li><a href="mailto:pelpartnercompany@gmail.com" className="flex items-center gap-2 hover:text-yellow-400 transition-colors cursor-pointer break-all"><i className="ri-mail-line text-yellow-500"></i> pelpartnercompany@gmail.com</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-xs">{t('footer_copyright')}</p>
            <p className="text-gray-500 text-xs">{t('footer_tagline')}</p>
          </div>
        </div>
      </footer>

      {/* ── WHATSAPP BUTTON ── */}
      <a href="https://wa.me/32487261970" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-50 w-14 h-14 flex items-center justify-center bg-[#25D366] text-white rounded-full shadow-2xl hover:scale-110 transition-transform cursor-pointer" aria-label="Contact us on WhatsApp">
        <i className="ri-whatsapp-line text-2xl"></i>
      </a>
    </div>
  );
}
